/**
 * Lähteiden lajit (admins): how many of the dataset's sources have no kind yet, so the AI reads
 * them as research, and a button that tags them with the same tagger new sources get — the
 * admin-only knowledge.tag_sources job (specs/knowledge-kinds.md, step 6, #1370). The job's
 * stage shows while it runs; the result counts what each source became.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Tags } from 'lucide-react';
import { Button, Actions, StatusLine } from '../../../components/InstanceSettings/formPrimitives';
import { useTranslation } from '../../../hooks/useTranslation.jsx';
import { getTagSourcesCount, tagSources } from './datasetApi';
import { Section, SectionTitle, Lead, ErrorText } from './styles';

/** The job's stage key as a line the admin reads. */
function stageLine(stage, t) {
  const m = /^tag (\d+)\/(\d+)$/.exec(stage || '');
  if (m) return t('knowledgeKinds.tagging.stageTag', { done: m[1], total: m[2] });
  if (stage === 'headers') return t('knowledgeKinds.tagging.stageHeaders');
  return t('knowledgeKinds.tagging.starting');
}

export default function SourceKindTagging({ datasetId, onDone }) {
  const { t } = useTranslation();
  const [count, setCount] = useState(null);
  const [error, setError] = useState(null);
  const [stage, setStage] = useState(null);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    try {
      setCount(await getTagSourcesCount(datasetId));
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [datasetId]);

  useEffect(() => { load(); }, [load]);

  // Leaving the workspace stops the polling, not the job
  const polling = useRef(null);
  useEffect(() => () => polling.current?.abort(), []);

  const run = async () => {
    const controller = new AbortController();
    polling.current = controller;
    const { signal } = controller;
    setStage('');
    setResult(null);
    setError(null);
    try {
      const done = await tagSources(datasetId, { onStage: setStage, signal });
      if (signal.aborted) return;
      setResult(done);
      onDone?.();
    } catch (err) {
      if (!signal.aborted) setError(err.message);
    } finally {
      if (!signal.aborted) {
        setStage(null);
        load();
      }
    }
  };

  const running = stage !== null;
  const work = count && (count.untagged > 0 || count.headers > 0);
  if (!error && !running && !result && !work) return null;

  const tagged = result?.tagged || {};
  return (
    <Section>
      <SectionTitle><Tags size={18} /> {t('knowledgeKinds.tagging.title')}</SectionTitle>
      {count?.untagged > 0 && <Lead>{t('knowledgeKinds.tagging.lead', { untagged: count.untagged })}</Lead>}
      {!count?.untagged && count?.headers > 0 && <Lead>{t('knowledgeKinds.tagging.headersOnly', { headers: count.headers })}</Lead>}
      {result && (
        <Lead>
          {t('knowledgeKinds.tagging.done', {
            research: tagged.fact || 0, policy: tagged.policy || 0, opinion: tagged.opinion || 0,
            unclassified: result.unclassified || 0, failed: result.failed?.length || 0,
          })}
        </Lead>
      )}
      {error && <ErrorText>{error}</ErrorText>}
      <Actions>
        {work && (
          <Button type="button" $variant="primary" disabled={running} onClick={run}>
            {t('knowledgeKinds.tagging.start')}
          </Button>
        )}
        {running && <StatusLine>{stageLine(stage, t)}</StatusLine>}
      </Actions>
    </Section>
  );
}
